// src/pages/TeacherAllStudents.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Alert,
  Typography,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Card,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { onAuthStateChanged } from "firebase/auth";
import TeacherModifyStudentModal from "../components/TeacherModifyStudentModal";
import TeacherNewStudentForm from "../components/TeacherNewStudentForm";

import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import TuneIcon from "@mui/icons-material/Tune";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import StraightenRoundedIcon from "@mui/icons-material/StraightenRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import FitnessCenterRoundedIcon from "@mui/icons-material/FitnessCenterRounded";

import TeacherNavbar from "../components/TeacherNavbar";
import group01 from "../img/groupGallery/group-01.jpeg";
import group02 from "../img/groupGallery/group-02.jpg";
import group03 from "../img/groupGallery/group-03.png";
import group04 from "../img/groupGallery/group-04.jpg";
import group05 from "../img/groupGallery/group-05.png";
import group06 from "../img/groupGallery/group-06.png";

import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

import { SUBJECTS } from "../constants";
import { useTranslation } from "react-i18next";
import { getRoleLabels, getStoredTutorType, setStoredTutorType } from "../utils/roleTerminology";
import { deleteStudentAccount } from "../services/studentService";

const AUTO_GROUP_IMAGE_POOL = [group01, group02, group03, group04, group05, group06];

function resolveApiBaseUrl() {
  return String(
    process.env.REACT_APP_API_BASE_URL ||
      "https://us-central1-houari-boumedienne-schoo-4d4c2.cloudfunctions.net",
  ).replace(/\/+$/, "");
}

function TeacherStudentsPage() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language || "ar") === "rtl";
  const intakeDirection = isRtl ? "rtl" : "ltr";
  const intakeTextAlign = isRtl ? "right" : "left";
  const [currentTutorType, setCurrentTutorType] = useState(getStoredTutorType());
  const role = getRoleLabels(t, currentTutorType);
  const [studentModalOpen, setStudentModalOpen] = useState(false);

  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [teachers, setTeachers] = useState([]); 
  const [teacherDocId, setTeacherDocId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [modifyStudentModalOpen, setModifyStudentModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeTab, setActiveTab] = useState("validated");
  const [intakeByEmail, setIntakeByEmail] = useState({});
  const [intakeDialogOpen, setIntakeDialogOpen] = useState(false);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const teacherUid = auth.currentUser?.uid;

  const teacherSubjectIds = useMemo(() => {
    return teachers?.[0]?.subjectIds || [];
  }, [teachers]);

  const getCurrentTeacherDoc = async (authUid) => {
    const q = query(collection(db, "teachers"), where("uid", "==", authUid));
    const snap = await getDocs(q);

    if (snap.empty) return null;

    const docSnap = snap.docs[0];
    return { id: docSnap.id, ...docSnap.data() }; // id = teacherDocId
    };

  // 1) Load teacher + teacher groups + teacher students
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
        try {
        setLoading(true);

        if (!user) {
            setStudents([]);
            setGroups([]);
            setTeachers([]);
            setFilteredStudents([]);
            setTeacherDocId(null);
            setLoading(false);
            return;
        }

        // 1) Get teacher document by auth uid
        const teacherDoc = await getCurrentTeacherDoc(user.uid);

        if (!teacherDoc) {
            console.warn("No teacher doc found for this user.uid:", user.uid);
            setStudents([]);
            setGroups([]);
            setTeachers([]);
            setFilteredStudents([]);
            setTeacherDocId(null);
            setLoading(false);
            return;
        }

        // IMPORTANT: This is the ID saved inside enrollments.teacherId in your DB
        const tDocId = teacherDoc.id;
        setTeacherDocId(tDocId);
        const resolvedTutorType = teacherDoc?.tutorType || "Teacher";
        setCurrentTutorType(resolvedTutorType);
        setStoredTutorType(resolvedTutorType);

        // 2) Teachers array (for NewStudentForm dropdown) - only current teacher
        setTeachers([
            {
            id: tDocId,
            firstName: teacherDoc.firstName || "",
            lastName: teacherDoc.lastName || "",
            subjectIds: teacherDoc.subjectIds || [],
            },
        ]);

        // 3) Load ONLY groups belonging to this teacher (teacherId == teacherDocId)
        const groupsQ = query(collection(db, "groups"), where("teacherId", "==", tDocId));
        const groupsSnap = await getDocs(groupsQ);
        const groupsData = groupsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setGroups(groupsData);

        // 4) Load students and filter by enrollment.teacherId == teacherDocId
        const studentsSnap = await getDocs(collection(db, "students"));
        const allStudents = studentsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const myStudents = allStudents.filter((s) => {
          const inEnrollments =
            Array.isArray(s.enrollments) &&
            s.enrollments.some((enr) => String(enr.teacherId) === String(tDocId));
          const byTeacherUid = s.teacherUid && String(s.teacherUid) === String(user.uid);
          return inEnrollments || byTeacherUid;
        });

        setStudents(myStudents);

        const intakeQ = query(
          collection(db, "programIntakes"),
          where("teacherUid", "==", user.uid)
        );
        const intakeSnap = await getDocs(intakeQ);
        const intakeMap = {};
        intakeSnap.forEach((d) => {
          const data = d.data();
          const emailKey = (data.studentEmail || data.leadEmail || "").trim().toLowerCase();
          if (!emailKey) return;
          const createdAtMs = data?.createdAt?.toMillis ? data.createdAt.toMillis() : 0;
          const existing = intakeMap[emailKey];
          if (!existing || createdAtMs > (existing._createdAtMs || 0)) {
            intakeMap[emailKey] = { id: d.id, ...data, _createdAtMs: createdAtMs };
          }
        });
        setIntakeByEmail(intakeMap);

        // Optional debug (remove later)
        console.log("AUTH uid:", user.uid);
        console.log("TeacherDocId:", tDocId);
        console.log("All students:", allStudents.length);
        console.log("My students:", myStudents.length);
        console.log("Sample enrollment teacherId:", allStudents[0]?.enrollments?.[0]?.teacherId);
        } catch (err) {
        console.error("Error loading teacher students:", err);
        setStudents([]);
        setGroups([]);
        setTeachers([]);
        setFilteredStudents([]);
        setTeacherDocId(null);
        } finally {
        setLoading(false);
        }
    });

    return () => unsub();
  }, []);

  // 2) Filter by tab + search (same as admin)
  useEffect(() => {
    const base = students.filter(
      (s) => s.adminValidated === (activeTab === "validated")
    );

    if (!searchTerm) {
      setFilteredStudents(base);
      return;
    }

    const q = searchTerm.toLowerCase();
    setFilteredStudents(
      base.filter(
        (s) =>
          s.firstName?.toLowerCase().includes(q) ||
          s.lastName?.toLowerCase().includes(q) ||
          s.email?.toLowerCase().includes(q)
      )
    );
  }, [students, activeTab, searchTerm]);

  // ---------- DASHBOARD ANALYTICS ----------
  const totalStudents = students.length;

  const activeStudents = students.filter((s) => s.adminValidated === true).length;

  const waitingStudents = students.filter((s) => s.adminValidated === false).length;

  const now = useMemo(() => new Date(), []);
  const last7Days = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  }, []);

  const last30Days = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }, []);

  const newLast7Days = students.filter((s) => new Date(s.entryDate) >= last7Days).length;

  const newLast30Days = students.filter((s) => new Date(s.entryDate) >= last30Days).length;

  // 3) Delete a student (and remove them from their groups)
  const handleDeleteStudent = async (studentId) => {
    const confirmDelete = window.confirm(
      role.isCoach
        ? t("Are you sure you want to delete this client?")
        : t("Êtes-vous sûr de vouloir supprimer cet étudiant ?")
    );
    if (!confirmDelete) return;

    try {
      const studentRef = doc(db, "students", studentId);
      const studentSnapshot = await getDoc(studentRef);
      const studentData = studentSnapshot.exists() ? studentSnapshot.data() : null;

      if (!studentData) {
        alert(role.isCoach ? t("Unable to find the client's data.") : t("Impossible de trouver les données de l'étudiant."));
        return;
      }

      const enrollmentGroupIds = Array.from(
        new Set((studentData.enrollments || []).map((enr) => enr?.groupId).filter(Boolean))
      );

      if (role.isCoach) {
        // Coach flow: deleting a client also removes that client's enrolled groups.
        await Promise.all(enrollmentGroupIds.map((groupId) => deleteDoc(doc(db, "groups", groupId))));

        if (teacherDocId) {
          const teacherRef = doc(db, "teachers", teacherDocId);
          const teacherSnap = await getDoc(teacherRef);
          if (teacherSnap.exists()) {
            const existingGroupIds = Array.isArray(teacherSnap.data()?.groupIds)
              ? teacherSnap.data().groupIds
              : [];
            const updatedGroupIds = existingGroupIds.filter((gid) => !enrollmentGroupIds.includes(gid));
            await updateDoc(teacherRef, { groupIds: updatedGroupIds });
          }
        }

        setGroups((prev) => prev.filter((g) => !enrollmentGroupIds.includes(g.id)));
      } else {
        const groupUpdates = enrollmentGroupIds.map((groupId) =>
          updateDoc(doc(db, "groups", groupId), {
            studentIds: arrayRemove(studentId),
          })
        );
        await Promise.all(groupUpdates);
      }

      await deleteStudentAccount(studentId);

      setStudents((prev) => prev.filter((s) => s.id !== studentId));
      setFilteredStudents((prev) => prev.filter((s) => s.id !== studentId));

      alert(role.isCoach ? t("Client deleted successfully.") : t("Étudiant supprimé avec succès."));
    } catch (error) {
      console.error("Erreur lors de la suppression de l'étudiant :", error);
      alert(t("Une erreur est survenue."));
    }
  };

  const getUniqueCoachGroupName = async (baseName) => {
    const root = String(baseName || "").trim() || t("Client");
    let candidate = root;
    let idx = 2;

    while (true) {
      const q = query(collection(db, "groups"), where("name", "==", candidate));
      const snap = await getDocs(q);
      if (snap.empty) return candidate;

      candidate = `${root} (${idx})`;
      idx += 1;
      if (idx > 100) throw new Error("Unable to generate a unique group name.");
    }
  };

  const ensureCoachEnrollmentOnApproval = async (student) => {
    if (!role.isCoach) return { studentWithEnrollment: student, createdGroup: null };
    if (!teacherDocId) return { studentWithEnrollment: student, createdGroup: null };

    const alreadyInMyGroup = Array.isArray(student?.enrollments) &&
      student.enrollments.some((enr) => String(enr?.teacherId) === String(teacherDocId));
    if (alreadyInMyGroup) {
      return { studentWithEnrollment: student, createdGroup: null };
    }

    const subjectFromAssignment = (SUBJECTS || []).find((s) => (teacherSubjectIds || []).includes(s.id));
    const fallbackGroup = (groups || []).find(
      (g) => String(g?.teacherId) === String(teacherDocId) && g?.subjectId
    );
    const subject =
      subjectFromAssignment ||
      (fallbackGroup
        ? {
            id: fallbackGroup.subjectId,
            name: fallbackGroup.subjectName || fallbackGroup.subjectId,
            level: fallbackGroup.subjectLevel || fallbackGroup.level || "",
          }
        : null);

    if (!subject?.id) {
      throw new Error("Coach has no assigned subject to create a client group.");
    }

    const fullName = `${student?.firstName || ""} ${student?.lastName || ""}`.trim();
    const fallbackName = (student?.email || "").trim() || t("Client");
    const groupName = await getUniqueCoachGroupName(fullName || fallbackName);
    const entryDate = new Date().toISOString();
    const randomGroupImage =
      AUTO_GROUP_IMAGE_POOL[Math.floor(Math.random() * AUTO_GROUP_IMAGE_POOL.length)] || "";

    const newGroup = {
      name: groupName,
      level: subject.level || "",
      subjectId: subject.id,
      teacherId: teacherDocId,
      subjectName: subject.name || "",
      subjectLevel: subject.level || "",
      studentIds: [student.id],
      entryDate,
      groupImage: randomGroupImage,
      autoCreated: true,
      groupType: "coach_1on1",
    };

    const groupRef = await addDoc(collection(db, "groups"), newGroup);
    await updateDoc(doc(db, "teachers", teacherDocId), {
      groupIds: arrayUnion(groupRef.id),
    });

    const enrollment = {
      groupId: groupRef.id,
      groupName: newGroup.name,
      subjectId: newGroup.subjectId,
      teacherId: teacherDocId,
      entryDate,
    };

    const nextEnrollments = [...(student?.enrollments || []), enrollment];
    await updateDoc(doc(db, "students", student.id), {
      enrollments: nextEnrollments,
    });

    return {
      studentWithEnrollment: { ...student, enrollments: nextEnrollments },
      createdGroup: { id: groupRef.id, ...newGroup },
    };
  };

  // 4) Approve students (same functionality)
  const handleApproveStudent = async (student) => {
    try {
      const ref = doc(db, "students", student.id);
      await updateDoc(ref, { adminValidated: true });

      let updatedStudent = { ...student, adminValidated: true };

      if (role.isCoach) {
        const { studentWithEnrollment, createdGroup } =
          await ensureCoachEnrollmentOnApproval(updatedStudent);
        updatedStudent = { ...studentWithEnrollment, adminValidated: true };

        if (createdGroup) {
          setGroups((prev) => [...(prev || []), createdGroup]);
        }
      }

      setStudents((prev) => prev.map((s) => (s.id === student.id ? updatedStudent : s)));

      const studentEmailKey = (updatedStudent.email || "").trim().toLowerCase();
      const linkedIntake = intakeByEmail[studentEmailKey] || null;

      try {
        const endpoint =
          `${resolveApiBaseUrl()}/api/program-intakes/confirm-student-registration`;
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            teacherUid: auth.currentUser?.uid || "",
            intakeId: linkedIntake?.id || "",
            studentEmail: studentEmailKey,
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.ok) {
          throw new Error(
            payload?.error || t("Student confirmed, but email/receipt cleanup failed."),
          );
        }

        if (studentEmailKey) {
          setIntakeByEmail((prev) => {
            if (!prev[studentEmailKey]) return prev;
            return {
              ...prev,
              [studentEmailKey]: {
                ...prev[studentEmailKey],
                paymentReceipts: [],
                paymentReceiptUploadLink: null,
                receiptNotificationLastSentAt: null,
              },
            };
          });
        }

        setSelectedIntake((prev) => {
          if (!prev) return prev;
          const matchById = linkedIntake?.id && prev.id === linkedIntake.id;
          const matchByEmail =
            (prev.leadEmail || prev.studentEmail || "").trim().toLowerCase() ===
            studentEmailKey;
          if (!matchById && !matchByEmail) return prev;
          return {
            ...prev,
            paymentReceipts: [],
            paymentReceiptUploadLink: null,
            receiptNotificationLastSentAt: null,
          };
        });
      } catch (notifyError) {
        console.error("Error sending confirmation email / cleaning receipts:", notifyError);
      }

      if (role.isCoach) {
        return;
      }

      setSelectedStudent(updatedStudent);
      setModifyStudentModalOpen(true);
    } catch (error) {
      console.error("Error approving student:", error);
      alert(t("حدث خطأ أثناء تأكيد الطالب. يرجى المحاولة مرة أخرى."));
    }
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const getStudentName = (s) =>
    `${s.firstName || ""} ${s.lastName || ""}`.trim() || (role.isCoach ? t("Unnamed client") : t("طالب بدون اسم"));

  const handleOpenEditStudent = (student) => {
    setSelectedStudent(student);
    setModifyStudentModalOpen(true);
  };

  const handleOpenIntake = (intake) => {
    setSelectedIntake(intake);
    setReceiptDialogOpen(false);
    setIntakeDialogOpen(true);
  };

  const handleCloseIntake = () => {
    setSelectedIntake(null);
    setReceiptDialogOpen(false);
    setIntakeDialogOpen(false);
  };

  const intakeAnswers = selectedIntake?.answers || {};
  const intakeReceipts = Array.isArray(selectedIntake?.paymentReceipts) ?
    [...selectedIntake.paymentReceipts].sort(
      (a, b) => Number(b?.createdAtMs || 0) - Number(a?.createdAtMs || 0)
    ) : [];
  const intakeFieldOrder = [
    "firstName",
    "lastName",
    "age",
    "gender",
    "wilayaCountry",
    "phoneCountryCode",
    "phone",
    "email",
    "maritalStatus",
    "job",
    "monthlyIncome",
    "weightKg",
    "heightCm",
    "neckCm",
    "waistCm",
    "armCm",
    "chestCm",
    "hipCm",
    "thighCm",
    "calfCm",
    "goal",
    "hasHealthIssues",
    "healthIssues",
    "smokingStatus",
    "inbodyDone",
    "inbodyFileUrl",
    "recentTestsDone",
    "recentTestsFileUrl",
    "hormones",
    "hormonesDetails",
    "medicationsTaking",
    "medications",
    "supplementsTaking",
    "supplements",
    "yesterdayMeals",
    "dietHistory",
    "favoriteFoods",
    "dislikedOrAllergyFoods",
    "hasKitchenScale",
    "sportNow",
    "sportSessionsPerWeek",
    "sportFollowUpInterest",
    "sportLocation",
    "sportSchedule",
    "bodyPhotoUrl",
  ];
  const intakeFieldLabels = {
    firstName: "الاسم",
    lastName: "اللقب",
    age: "العمر",
    gender: "الجنس",
    wilayaCountry: "الولاية / البلد",
    phoneCountryCode: "رمز الدولة",
    phone: "رقم الهاتف",
    email: "البريد الإلكتروني",
    maritalStatus: "الحالة الاجتماعية",
    job: "المهنة",
    monthlyIncome: "متوسط الدخل في الشهر",
    weightKg: "الوزن (كغ)",
    heightCm: "الطول (سم)",
    neckCm: "محيط الرقبة (سم)",
    waistCm: "مقاس محيط البطن (سم)",
    armCm: "مقاس الذراعين (سم)",
    chestCm: "مقاس الصدر (سم)",
    hipCm: "مقاس الأرداف (سم)",
    thighCm: "مقاس الفخذين (سم)",
    calfCm: "محيط عضلة الساق (سم)",
    goal: "الهدف من البرنامج",
    hasHealthIssues: "هل تعاني من أي مشكل صحي؟",
    healthIssues: "مشاكل صحية (إن وجدت)",
    smokingStatus: "هل أنت مدخن؟",
    inbodyDone: "هل قمت بتحليل Inbody؟",
    inbodyFileUrl: "تحميل نتيجة Inbody (اختياري إذا نعم)",
    recentTestsDone: "تحاليل حديثة (آخر 6 أشهر)؟",
    recentTestsFileUrl: "تحميل نتائج التحاليل (اختياري إذا نعم)",
    hormones: "هل تأخذ هرمونات أو أخذت من قبل؟",
    hormonesDetails: "ما هي الهرمونات؟ (إن وجدت)",
    medicationsTaking: "هل تأخذ أدوية؟",
    medications: "الأدوية (إن وجدت)",
    supplementsTaking: "هل تأخذ مكملات غذائية؟",
    supplements: "المكملات الغذائية (إن وجدت)",
    yesterdayMeals: "ماذا أكلت بالأمس بالتفصيل؟",
    dietHistory: "هل اتبعت نظام غذائي من قبل؟ هل كان مريح/قاسي؟ النتائج خلال شهر؟",
    favoriteFoods: "أكثر الأكلات/الأطباق التي تحبها",
    dislikedOrAllergyFoods: "أكلات لا تحبها أو لديك حساسية منها",
    hasKitchenScale: "هل عندك ميزان في المطبخ؟",
    sportNow: "هل تمارس رياضة حاليا؟ ما نوعها؟ منذ متى؟",
    sportSessionsPerWeek: "كم حصة رياضة بالأسبوع حالياً؟",
    sportFollowUpInterest: "هل أنت مهتم بمتابعة رياضية إلى جانب التغذية؟",
    sportLocation: "مكان التمرين (إذا مهتم بالمتابعة الرياضية)",
    sportSchedule: "حدد الأيام والتوقيت للتمرين",
    bodyPhotoUrl: "صورة شكل الجسم (إذا متابعة رياضية)",
  };
  const intakeFieldOrderIndex = new Map(intakeFieldOrder.map((k, i) => [k, i]));
  const intakeAnswerEntries = Object.entries(intakeAnswers).filter(
    ([key, value]) =>
      key !== "fullName" &&
      value !== null &&
      value !== undefined &&
      value !== ""
  );
  const orderedIntakeAnswerEntries = [...intakeAnswerEntries].sort(([a], [b]) => {
    const ia = intakeFieldOrderIndex.has(a) ? intakeFieldOrderIndex.get(a) : Number.MAX_SAFE_INTEGER;
    const ib = intakeFieldOrderIndex.has(b) ? intakeFieldOrderIndex.get(b) : Number.MAX_SAFE_INTEGER;
    if (ia !== ib) return ia - ib;
    return 0;
  });
  const formatIntakeLabel = (rawKey) =>
    String(rawKey || "")
      .replace(/([A-Z])/g, " $1")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const isHttpUrl = (candidate) =>
    typeof candidate === "string" && /^https?:\/\/\S+$/i.test(candidate.trim());
  const getFileLinksFromValue = (rawValue) => {
    if (Array.isArray(rawValue) && rawValue.length > 0) {
      const normalized = rawValue.map((item) => String(item || "").trim());
      return normalized.every(isHttpUrl) ? normalized : [];
    }
    const single = String(rawValue || "").trim();
    return isHttpUrl(single) ? [single] : [];
  };
  const renderIntakeValue = (rawValue) => {
    const links = getFileLinksFromValue(rawValue);
    if (links.length > 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.8,
            alignItems: isRtl ? "flex-end" : "flex-start",
          }}
        >
          {links.map((url, idx) => (
            <Button
              key={`${url}-${idx}`}
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="small"
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                px: 1.2,
                py: 0.45,
                borderColor: "#cbd5e1",
                color: "#334155",
                "&:hover": {
                  borderColor: "#94a3b8",
                  backgroundColor: "#f1f5f9",
                },
              }}
            >
              {t("افتح الرابط مباشرة")}
            </Button>
          ))}
        </Box>
      );
    }

    return (
      <Typography sx={{ color: "#475569", fontSize: 14, wordBreak: "break-word", textAlign: intakeTextAlign }}>
        {Array.isArray(rawValue) ? rawValue.join(", ") : String(rawValue)}
      </Typography>
    );
  };
  const getIntakeIconForKey = (rawKey) => {
    const key = String(rawKey || "");

    const identityKeys = new Set([
      "firstName",
      "lastName",
      "age",
      "gender",
      "wilayaCountry",
      "phoneCountryCode",
      "phone",
      "email",
      "maritalStatus",
      "job",
      "monthlyIncome",
      "fullName",
    ]);
    const measuresKeys = new Set([
      "weightKg",
      "heightCm",
      "neckCm",
      "waistCm",
      "armCm",
      "chestCm",
      "hipCm",
      "thighCm",
      "calfCm",
    ]);
    const healthKeys = new Set([
      "goal",
      "hasHealthIssues",
      "healthIssues",
      "smokingStatus",
      "inbodyDone",
      "inbodyFileUrl",
      "recentTestsDone",
      "recentTestsFileUrl",
      "hormones",
      "hormonesDetails",
      "medicationsTaking",
      "medications",
      "supplementsTaking",
      "supplements",
    ]);
    const nutritionKeys = new Set([
      "yesterdayMeals",
      "dietHistory",
      "favoriteFoods",
      "dislikedOrAllergyFoods",
      "hasKitchenScale",
    ]);
    const sportKeys = new Set([
      "sportNow",
      "sportSessionsPerWeek",
      "sportFollowUpInterest",
      "sportLocation",
      "sportSchedule",
      "bodyPhotoUrl",
    ]);

    if (identityKeys.has(key)) return <PersonOutlineRoundedIcon sx={{ fontSize: 18 }} />;
    if (measuresKeys.has(key)) return <StraightenRoundedIcon sx={{ fontSize: 18 }} />;
    if (healthKeys.has(key)) return <FavoriteBorderRoundedIcon sx={{ fontSize: 18 }} />;
    if (nutritionKeys.has(key)) return <RestaurantMenuRoundedIcon sx={{ fontSize: 18 }} />;
    if (sportKeys.has(key)) return <FitnessCenterRoundedIcon sx={{ fontSize: 18 }} />;
    return <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />;
  };

  return (
    <div
      style={{
        background:
          'radial-gradient(1200px 600px at 10% -10%, rgba(255, 204, 155, 0.35), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(130, 189, 255, 0.35), transparent 60%), #f6f7fb',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Space Grotesk", "Work Sans", system-ui, sans-serif',
        overflowX: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Top bar */}
      <TeacherNavbar />

      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 64px)",
          backgroundColor: "transparent",
          flexDirection: "row-reverse",
          overflow: "hidden",
        }}
      >

        {/* LEFT: Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1, md: 2 },
            overflowY: "auto",
          }}
        >
          {/* Header card */}
          <Card
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: 4,
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            {/* LEFT SIDE — BUTTONS */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" size="small">
                <ImportExportIcon /> {t("تصدير")}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setStudentModalOpen(true)}
                startIcon={<PersonAddAlt1Icon />}
              >
                {role.isCoach ? t("Add client") : t("إضافة طالب")}
              </Button>
            </Box>

            {/* RIGHT SIDE — TITLE + SUBTITLE + ICON */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {role.learnerPlural}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {role.isCoach ? t("Manage your enrolled clients in your groups") : t("إدارة طلابك المسجلين في مجموعاتك")}
                </Typography>
              </Box>
              <Avatar
                sx={{
                  bgcolor: "#eef2ff",
                  color: "#4f46e5",
                  width: 46,
                  height: 46,
                  boxShadow: "0 8px 18px rgba(79,70,229,0.18)",
                }}
              >
                <PeopleAltIcon />
                
              </Avatar>
            </Box>
          </Card>

          {/* ===== STUDENTS DASHBOARD ANALYTICS ===== */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 2,
              mb: 3,
            }}
          >
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {totalStudents}
                </Typography>
                <Typography color="text.secondary">{role.isCoach ? t("Total Clients") : t("Total Students")}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: "#e0f2fe", color: "#0369a1", width: 56, height: 56 }}>
                <SchoolIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </Card>

            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {activeStudents}
                </Typography>
                <Typography color="text.secondary">{role.isCoach ? t("Active Clients") : t("Active Students")}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a", width: 56, height: 56 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </Card>

            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {waitingStudents}
                </Typography>
                <Typography color="text.secondary">{t("Waiting List")}</Typography>
              </Box>
              <Avatar sx={{ bgcolor: "#fff7ed", color: "#ea580c", width: 56, height: 56 }}>
                <PendingActionsIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </Card>

            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {newLast7Days}
                </Typography>
                <Typography color="text.secondary">
                  {t("New (7d) · {{count30}} (30d)", { count30: newLast30Days })}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: "#eef2ff", color: "#4f46e5", width: 56, height: 56 }}>
                <TrendingUpIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </Card>
          </Box>

          {/* Tabs row + Search/Filter */}
          <Box
            sx={{
              direction: "rtl",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2.5,
            }}
          >
            {/* RIGHT: Tabs */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 0.75,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                border: "1px solid rgba(0,0,0,0.06)",
                width: "fit-content",
              }}
            >
              <Button
                disableRipple
                onClick={() => setActiveTab("validated")}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontWeight: 700,
                  textTransform: "none",
                  backgroundColor: activeTab === "validated" ? "#eef2ff" : "transparent",
                  color: activeTab === "validated" ? "#4f46e5" : "text.secondary",
                }}
              >
                {role.isCoach ? t("Clients fully registered") : t("الطلاب المسجلين بالكامل")}
              </Button>

              <Button
                disableRipple
                onClick={() => setActiveTab("unvalidated")}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontWeight: 700,
                  textTransform: "none",
                  backgroundColor: activeTab === "unvalidated" ? "#fff7ed" : "transparent",
                  color: activeTab === "unvalidated" ? "#ea580c" : "text.secondary",
                }}
              >
                {role.isCoach ? t("Clients not fully registered") : t("الطلاب غير المسجلين بالكامل")}
              </Button>
            </Box>

            {/* LEFT: Filter + Search */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder={role.isCoach ? t("Search client by name or email...") : t("ابحث عن طالب بالاسم أو البريد...")}
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{
                  width: 320,
                  backgroundColor: "#f9fafb",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 2,
                  borderColor: "rgba(0,0,0,0.12)",
                  backgroundColor: "#fff",
                  minWidth: 42,
                  "&:hover": { backgroundColor: "#f9fafb", borderColor: "rgba(0,0,0,0.18)" },
                }}
              >
                <TuneIcon sx={{ color: "rgba(0,0,0,0.6)" }} />
              </Button>
            </Box>
          </Box>

          {/* Students table */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "#ffffff",
              boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Table sx={{ direction: "rtl" }}>
              <TableHead sx={{ bgcolor: "#ffffffff" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{role.isCoach ? t("Client name") : t("اسم الطالب")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("المستوى")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("القسم")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("البريد الإلكتروني")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("الهاتف")}</TableCell>
                  {activeTab === "unvalidated" && (
                    <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("مدفوع")}</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("الحالة")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: "right" }}>{t("الإجراءات")}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {role.isCoach ? t("Loading clients list...") : t("جاري تحميل قائمة الطلاب...")}
                    </TableCell>
                  </TableRow>
                ) : filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {role.isCoach ? t("No clients match your search.") : t("لا يوجد طلاب مطابقون لعملية البحث.")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.map((student) => {
                    const studentEmailKey = (student.email || "").trim().toLowerCase();
                    const intake = intakeByEmail[studentEmailKey];
                    return (
                      <TableRow
                        key={student.id}
                        hover
                        sx={{
                          direction: "rtl",
                          "& td": { py: 2 },
                          "&:hover": { backgroundColor: "#fafafa" },
                        }}
                      >
                      {/* 1 — NAME */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexDirection: "row-reverse" }}>
                          <Box>
                            <Typography sx={{ fontWeight: 500 }}>{getStudentName(student)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {student.studentCode || ""}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* 2 — LEVEL */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Chip
                          label={student.level || t("غير محدد")}
                          size="small"
                          sx={{ bgcolor: "#e0f2fe", color: "#0369a1", borderRadius: "999px" }}
                        />
                      </TableCell>

                      {/* 3 — GROUP (show first enrollment groupName) */}
                      <TableCell sx={{ textAlign: "right" }}>
                        {student.enrollments?.length > 0
                          ? student.enrollments[0].groupName || student.enrollments[0].groupId
                          : "-"}
                      </TableCell>

                      {/* 4 — EMAIL */}
                      <TableCell sx={{ textAlign: "right" }}>{student.email || "-"}</TableCell>

                      {/* 5 — PHONE */}
                      <TableCell sx={{ textAlign: "right" }}>{student.phone || "-"}</TableCell>

                      {/* 6 — PAID */}
                      {activeTab === "unvalidated" && (
                        <TableCell sx={{ textAlign: "right" }}>
                          <Chip
                            label={t("Not paid")}
                            size="small"
                            sx={{
                              bgcolor: "rgba(239,68,68,0.12)",
                              color: "rgba(185,28,28,0.95)",
                              borderRadius: "5px",
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                      )}

                      {/* 7 — STATUS */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Chip
                          label={student.adminValidated ? t("ACTIVE") : t("WAITING")}
                          size="small"
                          sx={{
                            bgcolor: student.adminValidated
                              ? "rgba(34,197,94,0.12)"
                              : "rgba(251,191,36,0.18)",
                            color: student.adminValidated
                              ? "rgba(22,163,74,0.95)"
                              : "#b45309",
                            borderRadius: "5px",
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>

                      {/* 8 — ACTIONS */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Box sx={{ display: "flex", flexDirection: "row-reverse", gap: 0.5 }}>
                          <Button
                            variant="text"
                            size="small"
                            color="error"
                            onClick={() => handleDeleteStudent(student.id)}
                            sx={{ fontWeight: 700, minWidth: 0, px: 1 }}
                            title={t("حذف")}
                          >
                            <DeleteOutlinedIcon />
                          </Button>

                          {student.adminValidated === true && (
                            <Button
                              size="small"
                              onClick={() => handleOpenEditStudent(student)}
                              sx={{
                                fontWeight: 800,
                                minWidth: 0,
                                px: 1,
                                "&:hover": { backgroundColor: "#f9fafb" },
                              }}
                              title={role.isCoach ? t("Edit client") : t("تعديل الطالب")}
                            >
                              <BorderColorOutlinedIcon sx={{ color: "#50667dff" }} />
                            </Button>
                          )}

                          {intake && (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => handleOpenIntake(intake)}
                              sx={{
                                fontWeight: 800,
                                minWidth: 0,
                                px: 1,
                                color: "#0ea5e9",
                                "&:hover": { backgroundColor: "rgba(14,165,233,0.10)" },
                              }}
                              title={t("عرض الاستبيان")}
                            >
                              <DescriptionOutlinedIcon />
                            </Button>
                          )}

                          {student.adminValidated === false && (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => handleApproveStudent(student)}
                              sx={{
                                fontWeight: 800,
                                color: "rgba(22,163,74,0.95)",
                                "&:hover": { backgroundColor: "rgba(34,197,94,0.10)" },
                                minWidth: 0,
                                px: 1,
                              }}
                              title={role.isCoach ? t("Confirm client") : t("تأكيد الطالب")}
                            >
                              <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Add student modal (same UI, but teacher-scoped data passed) */}
          <Dialog
            open={studentModalOpen}
            onClose={() => setStudentModalOpen(false)}
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
              },
            }}
          >
            <DialogContent
              sx={{
                p: { xs: 2, md: 3 },
                bgcolor: "background.default",
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                "& .MuiInputBase-root": { borderRadius: 2 },
                "& .MuiButton-containedPrimary": {
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                },
                "& .MuiButton-outlinedPrimary": { borderRadius: 2 },
              }}
            >
                <TeacherNewStudentForm
                onClose={() => setStudentModalOpen(false)}
                onCreated={(newStudent) => {
                    const isMine =
                    Array.isArray(newStudent.enrollments) &&
                    newStudent.enrollments.some(
                        (e) => String(e.teacherId) === String(teacherDocId)
                    );

                    if (isMine) {
                    setStudents((prev) => [...prev, newStudent]);
                    setFilteredStudents((prev) => [...prev, newStudent]);
                    }
                }}
                teacherId={teacherDocId}
                teacherSubjectIds={teacherSubjectIds}
                students={students}
                subjects={SUBJECTS || []}
                groups={groups || []}
                />
            </DialogContent>
          </Dialog>

          {selectedStudent && (
            <TeacherModifyStudentModal
                open={modifyStudentModalOpen}
                onClose={() => setModifyStudentModalOpen(false)}
                student={selectedStudent}
                teacherId={teacherDocId}
                teacherSubjectIds={teacherSubjectIds}
                groups={groups || []}
                setStudents={setStudents}
            />
          )}

          <Dialog
            open={intakeDialogOpen}
            onClose={handleCloseIntake}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
                border: "1px solid rgba(15, 23, 42, 0.08)",
                direction: intakeDirection,
              },
            }}
          >
            <DialogTitle
              sx={{
                bgcolor: "#ffffff",
                borderBottom: "1px solid #e5e7eb",
                py: 2.25,
                textAlign: intakeTextAlign,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isRtl ? "row-reverse" : "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 22, color: "#0f172a" }}>
                    {role.isCoach ? t("Client intake form") : t("استمارة الطالب")}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 13.5 }}>
                    {role.isCoach ? t("Registration form details and questionnaire answers") : t("تفاصيل نموذج التسجيل وإجابات الاستبيان")}
                  </Typography>
                </Box>
                <Chip
                  label={role.isCoach ? t("Client Form") : t("Student Form")}
                  sx={{
                    bgcolor: "#eef2ff",
                    color: "#3730a3",
                    fontWeight: 700,
                    borderRadius: "999px",
                  }}
                />
              </Box>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                bgcolor: "#f8fafc",
                p: { xs: 2, md: 3 },
                direction: intakeDirection,
              }}
            >
              {!selectedIntake ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2.5,
                    border: "1px solid #e5e7eb",
                    textAlign: "center",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Typography sx={{ color: "#64748b" }}>{t("لا توجد بيانات لعرضها.")}</Typography>
                </Paper>
              ) : (
                <Box sx={{ display: "grid", gap: 2 }}>
                  {intakeReceipts.length > 0 ? (
                    <Alert
                      severity="success"
                      action={(
                        <Button
                          color="inherit"
                          size="small"
                          variant="outlined"
                          onClick={() => setReceiptDialogOpen(true)}
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          {t("View receipts")}
                        </Button>
                      )}
                      sx={{ borderRadius: 2 }}
                    >
                      {t("Payment receipt submitted successfully.")} ({intakeReceipts.length})
                    </Alert>
                  ) : null}

                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      p: 2,
                      border: "1px solid #e5e7eb",
                      bgcolor: "#ffffff",
                      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                        gap: 1.25,
                      }}
                    >
                      {[
                        { label: t("الاسم"), value: selectedIntake.fullName || "-" },
                        { label: t("البريد الإلكتروني"), value: selectedIntake.leadEmail || selectedIntake.studentEmail || "-" },
                        { label: t("الهاتف"), value: selectedIntake.leadPhone || "-" },
                        {
                          label: t("الخطة"),
                          value:
                            selectedIntake.selectedServiceName ||
                            selectedIntake.selectedServiceId ||
                            selectedIntake.selectedPlan ||
                            "-",
                        },
                      ].map((item) => (
                        <Box
                          key={item.label}
                          sx={{
                            borderRadius: 2,
                            border: "1px solid #eef1f6",
                            bgcolor: "#f8fafc",
                            p: 1.25,
                          }}
                        >
                          <Typography sx={{ color: "#64748b", fontSize: 12.5, fontWeight: 700, mb: 0.25 }}>
                            {item.label}
                          </Typography>
                          <Typography sx={{ color: "#0f172a", fontSize: 14.5, fontWeight: 600, wordBreak: "break-word" }}>
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Card>

                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      p: 2,
                      border: "1px solid #e5e7eb",
                      bgcolor: "#ffffff",
                      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", mb: 1.5 }}>
                      {t("إجابات الاستمارة")}
                    </Typography>
                    {orderedIntakeAnswerEntries.length === 0 ? (
                      <Typography color="text.secondary">{t("لا توجد إجابات.")}</Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "grid",
                          gap: 1.1,
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            lg: "repeat(3, minmax(0, 1fr))",
                          },
                        }}
                      >
                        {orderedIntakeAnswerEntries.map(([key, value]) => (
                          <Box
                            key={key}
                            sx={{
                              borderRadius: 2,
                              border: "1px solid #eef1f6",
                              bgcolor: "#f8fafc",
                              p: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: isRtl ? "row-reverse" : "row",
                                alignItems: "center",
                                gap: 0.8,
                                mb: 0.35,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  display: "grid",
                                  placeItems: "center",
                                  color: "#0f172a",
                                  bgcolor: "#e9eef8",
                                  border: "1px solid #dbe3f2",
                                  flexShrink: 0,
                                }}
                              >
                                {getIntakeIconForKey(key)}
                              </Box>
                              <Typography sx={{ color: "#334155", fontWeight: 700, fontSize: 13.5, textAlign: intakeTextAlign }}>
                                {t(intakeFieldLabels[key] || formatIntakeLabel(key))}
                              </Typography>
                            </Box>
                            {renderIntakeValue(value)}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Card>
                </Box>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                py: 1.5,
                borderTop: "1px solid #e5e7eb",
                bgcolor: "#ffffff",
              }}
            >
              <Button
                onClick={handleCloseIntake}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  bgcolor: "#0f172a",
                  textTransform: "none",
                  px: 2.5,
                  "&:hover": { bgcolor: "#1e293b" },
                }}
              >
                {t("إغلاق")}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={receiptDialogOpen}
            onClose={() => setReceiptDialogOpen(false)}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle sx={{ fontWeight: 800 }}>
              {t("Submitted receipts")}
            </DialogTitle>
            <DialogContent dividers sx={{ bgcolor: "#f8fafc" }}>
              {intakeReceipts.length === 0 ? (
                <Typography color="text.secondary">
                  {t("No receipts submitted yet.")}
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gap: 1.4,
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  }}
                >
                  {intakeReceipts.map((receipt, idx) => (
                    <Card
                      key={`${receipt?.url || "receipt"}-${idx}`}
                      variant="outlined"
                      sx={{ borderRadius: 2, overflow: "hidden" }}
                    >
                      {receipt?.url ? (
                        <Box
                          component="img"
                          src={receipt.url}
                          alt={receipt.fileName || `receipt-${idx + 1}`}
                          sx={{ width: "100%", height: 190, objectFit: "cover", bgcolor: "#e2e8f0" }}
                        />
                      ) : null}
                      <Box sx={{ p: 1.1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                          {receipt?.fileName || `Receipt ${idx + 1}`}
                        </Typography>
                        {receipt?.url ? (
                          <Button
                            href={receipt.url}
                            target="_blank"
                            rel="noreferrer"
                            size="small"
                            sx={{ mt: 0.4, p: 0, textTransform: "none" }}
                          >
                            {t("Open image")}
                          </Button>
                        ) : null}
                      </Box>
                    </Card>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
              <Button
                onClick={() => setReceiptDialogOpen(false)}
                variant="contained"
                sx={{ textTransform: "none" }}
              >
                {t("Close")}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </div>
  );
}

export default TeacherStudentsPage;
